import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να έρθω walk-in για τατουάζ στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το studio. Πολλά studios δέχονται walk-ins για απλά ή μικρά σχέδια, αλλά για custom ή πολύπλοκα tattoos απαιτείται ραντεβού και consultation." } },
    { "@type": "Question", name: "Γιατί χρειάζεται ραντεβού για custom tattoo;", acceptedAnswer: { "@type": "Answer", text: "Γιατί το custom design χρειάζεται χρόνο σχεδιασμού πριν τη συνεδρία. Ο artist πρέπει να φτιάξει το σχέδιο βάσει των δικών σου αναγκών, αυτό δεν γίνεται την ώρα που περιμένεις στην ουρά." } },
    { "@type": "Question", name: "Πόσο νωρίς πρέπει να κλείσω ραντεβού;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το πρόγραμμα του artist. Για custom δουλειά, 2–4 εβδομάδες είναι συνήθως αρκετά. Για πιο σύνθετα projects, ίσως περισσότερο." } },
  ],
};

export default function WalkInVsAppointmentAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Walk-in ή Ραντεβού; Τι να Επιλέξεις για Tattoo στην Αθήνα",
      "description": "Διαφορά walk-in και ραντεβού για τατουάζ: πότε να επιλέξεις το καθένα, τι να περιμένεις και πώς λειτουργεί το σύστημα στα σοβαρά studios της Αθήνας.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/walk-in-vs-appointment-athens",
      "inLanguage": "el"
    };
    inject('schema-walkin-appt-faq', faqSchema);
    inject('schema-walkin-appt-article', articleSchema);
    return () => {
      document.getElementById('schema-walkin-appt-faq')?.remove();
      document.getElementById('schema-walkin-appt-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Walk-in ή Ραντεβού; Τι να Επιλέξεις για Tattoo στην Αθήνα | 210 Tattoo Athens</title>
        <meta name="description" content="Διαφορά walk-in και ραντεβού για τατουάζ: πότε να επιλέξεις το καθένα και πώς λειτουργεί στα studios της Αθήνας." />
        <link rel="canonical" href="https://210tattoo.gr/blog/walk-in-vs-appointment-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/walk-in-vs-appointment-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/walk-in-vs-appointment-athens" />
        <meta property="og:title" content="Walk-in ή Ραντεβού; Τι να Επιλέξεις για Tattoo στην Αθήνα | 210 Tattoo Athens" />
        <meta property="og:description" content="Walk-in vs ραντεβού: πότε να επιλέξεις το καθένα για τατουάζ στην Αθήνα." />
        <meta property="og:url" content="https://210tattoo.gr/blog/walk-in-vs-appointment-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('walk-in-vs-appointment-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Walk-in ή Ραντεβού για Tattoo; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Πότε να πας walk-in και πότε να κλείσεις ραντεβού για τατουάζ στην Αθήνα." />
        <meta name="twitter:image" content={ogImage('walk-in-vs-appointment-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Walk-in ή Ραντεβού</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Walk-in ή ραντεβού;</span>
              <br />
              <span className="text-white">Τι να επιλέξεις για tattoo στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η διαφορά μεταξύ walk-in και ραντεβού αντικατοπτρίζει ολόκληρο τον τρόπο που ένα studio δουλεύει και τι μπορείς να περιμένεις από τη συνεδρία σου.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What walk-in means */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι σημαίνει <span className="text-pink-500">walk-in</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Walk-in σημαίνει ότι μπαίνεις χωρίς προηγούμενο ραντεβού και ρωτάς αν υπάρχει διαθεσιμότητα. Σε κάποια studios λειτουργεί, αλλά με συγκεκριμένους περιορισμούς.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Κατάλληλο για απλά σχέδια', desc: 'Αν θέλεις κάτι μικρό από το flash book του studio ή ένα γρήγορο lettering, το walk-in μπορεί να λειτουργήσει.' },
                { title: 'Εξαρτάται από διαθεσιμότητα', desc: 'Δεν υπάρχει εγγύηση ότι θα σε δεχτούν. Ο artist μπορεί να είναι απασχολημένος ή να μην έχει χρόνο για τη δουλειά σου.' },
                { title: 'Δεν υπάρχει custom design', desc: 'Ένα walk-in δεν αφήνει χρόνο για σχεδιασμό. Αν θες κάτι φτιαγμένο για σένα, το walk-in δεν είναι η σωστή επιλογή.' },
                { title: 'Περισσότερη αβεβαιότητα', desc: 'Δεν ξέρεις αν θα γίνει σήμερα, αν θα πάρεις τον artist που θέλεις ή αν θα υπάρχει αρκετός χρόνος για σωστή δουλειά.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What appointment means */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι σημαίνει <span className="text-pink-500">ραντεβού με consultation</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ραντεβού σημαίνει ότι ο artist ξέρει τι έρχεσαι να κάνεις, έχει χρόνο να ετοιμαστεί και η συνεδρία ξεκινά με σαφή εικόνα από και τις δύο πλευρές. Αυτό ισχύει ανεξάρτητα από το αν το design είναι απλό ή πολύπλοκο. Ακόμα και για ένα μικρό custom κομμάτι, η προετοιμασία δίνει καλύτερο αποτέλεσμα από μια αυθόρμητη επίσκεψη.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Το σχέδιο είναι έτοιμο όταν έρθεις', desc: 'Ο artist έχει δουλέψει το custom design βάσει της συζήτησης σας. Δεν χάνεις χρόνο και δεν υπάρχουν εκπλήξεις.' },
                { title: 'Έχεις αποκλειστικό χρόνο', desc: 'Το ραντεβού σου σημαίνει ότι ο artist έχει αφιερώσει αυτές τις ώρες αποκλειστικά για σένα.' },
                { title: 'Η επικοινωνία γίνεται πριν τη συνεδρία', desc: 'Τυχόν αλλαγές, απορίες ή αναπροσαρμογές γίνονται πριν, όχι πάνω στο τατουάζ.' },
                { title: 'Καλύτερο αποτέλεσμα', desc: 'Ένας artist που ξέρει τι θα κάνει πριν ανοίξει τη μηχανή δουλεύει με περισσότερη αυτοπεποίθηση και καθαριότητα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When walk-in makes sense */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε το walk-in <span className="text-pink-500">έχει νόημα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν είναι πάντα λάθος. Υπάρχουν περιπτώσεις όπου το walk-in είναι απόλυτα κατάλληλο.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Flash tattoos', desc: 'Προσχεδιασμένα σχέδια που υπάρχουν στο studio, μπορούν να γίνουν σχετικά γρήγορα και χωρίς ιδιαίτερη προετοιμασία.' },
                { title: 'Μικρά, απλά σχέδια', desc: 'Αν ξέρεις ακριβώς τι θέλεις και είναι κάτι απλό, το walk-in μπορεί να γίνει αν υπάρχει χρόνος.' },
                { title: 'Touchup σε παλιό tattoo', desc: 'Μικρές επεμβάσεις σε υπάρχον tattoo μπορεί να γίνουν χωρίς εκτεταμένο σχεδιασμό.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips for walk-in */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να προσέξεις <span className="text-pink-500">αν δοκιμάσεις walk-in</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αν αποφασίσεις να πας walk-in, λίγη προετοιμασία αυξάνει σημαντικά τις πιθανότητες να ολοκληρωθεί η συνεδρία εκείνη τη μέρα.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Πήγαινε νωρίς', desc: 'Τα studios που δέχονται walk-ins έχουν συνήθως περιορισμένες θέσεις. Η εμφάνιση στην αρχή της βάρδιας αυξάνει τις πιθανότητες να βρεθεί χρόνος για σένα.' },
                { title: 'Έχε κάτι σαφές στο μυαλό σου', desc: 'Το walk-in δεν αφήνει χρόνο για εκτεταμένη συζήτηση ιδεών. Όσο πιο σαφής είσαι για το τι θέλεις, τόσο πιο εύκολο για τον artist να αξιολογήσει αν μπορεί να το κάνει εκείνη τη μέρα.' },
                { title: 'Μην έχεις προσδοκία για συγκεκριμένο artist', desc: 'Στο walk-in παίρνεις τον artist που έχει διαθεσιμότητα εκείνη τη στιγμή. Αν θέλεις να δουλέψεις με κάποιον συγκεκριμένο, το ραντεβού είναι ο μόνος τρόπος να το εξασφαλίσεις.' },
                { title: 'Να είσαι ανοιχτός να επιστρέψεις', desc: 'Μπορεί να φτάσεις και το studio να μην έχει χρόνο εκείνη τη μέρα. Στα walk-ins αυτό συμβαίνει συχνά. Είναι χρήσιμο να έχεις εναλλακτική μέρα στο πίσω μέρος του μυαλού σου.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How consultation works */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς λειτουργεί η <span className="text-pink-500">διαδικασία ραντεβού</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Για custom ή πολύπλοκα tattoos, η διαδικασία ξεκινά πάντα με consultation, και αυτό είναι στην πραγματικότητα μέρος της δουλειάς.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Επικοινωνία και consultation', desc: 'Στέλνεις μήνυμα με την ιδέα σου, το placement και τυχόν αναφορές. Συζητάμε τι είναι εφικτό και τι κάνει νόημα.' },
                { step: '02', title: 'Κλείσιμο ραντεβού', desc: 'Μόλις συμφωνήσουμε στις βασικές παραμέτρους, κλείνουμε ημερομηνία. Συνήθως απαιτείται προκαταβολή για reservation.' },
                { step: '03', title: 'Σχεδιασμός πριν τη συνεδρία', desc: 'Ο artist ετοιμάζει το custom design βάσει αυτών που συζητήθηκαν. Μπορεί να υπάρξει ανατροφοδότηση πριν την ημέρα.' },
                { step: '04', title: 'Η συνεδρία με έτοιμο σχέδιο', desc: 'Έρχεσαι, βλέπεις το σχέδιο, γίνονται τυχόν μικροαλλαγές και ξεκινά η συνεδρία χωρίς χαμένο χρόνο.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς λειτουργεί <span className="text-pink-500">το 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο 210 Tattoo Athens δεχόμαστε walk-ins σχεδόν σε όλο μας το ωράριο, όχι μόνο ραντεβού. Αν έχεις μια ιδέα και θέλεις να την κάνεις πράξη άμεσα, μπορείς απλά να περάσεις από το studio.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Walk-in σχεδόν οποιαδήποτε στιγμή', desc: 'Δεν χρειάζεται να κλείσεις ραντεβού εκ των προτέρων. Αν υπάρχει διαθέσιμος artist τη στιγμή που περνάς, ξεκινάμε άμεσα.' },
                { title: 'Ένα τηλεφώνημα ή DM πριν έρθεις βοηθάει', desc: 'Επειδή η διαθεσιμότητα αλλάζει μέσα στη μέρα, ένα σύντομο μήνυμα στο Instagram ή ένα τηλεφώνημα πριν έρθεις σε βοηθάει να ξέρεις σίγουρα ότι θα σε εξυπηρετήσουμε αμέσως.' },
                { title: 'Ραντεβού για όποιον προτιμά προγραμματισμό', desc: 'Αν προτιμάς να κλείσεις συγκεκριμένη μέρα και ώρα, ειδικά για πιο σύνθετα custom κομμάτια, το ραντεβού παραμένει διαθέσιμο με τον ίδιο τρόπο όπως περιγράφηκε παραπάνω.' },
                { title: 'Ίδια πρωτόκολλα υγιεινής και ποιότητας', desc: 'Είτε έρθεις walk-in είτε με ραντεβού, ακολουθούμε τα ίδια αυστηρά πρωτόκολλα υγιεινής και τα ίδια standards ποιότητας σε κάθε συνεδρία.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed mt-8">
              Δες όλες τις λεπτομέρειες στη σελίδα μας για <a href="/walk-in-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">walk-in τατουάζ στην Αθήνα</a>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έτοιμος να κλείσεις <span className="text-pink-500">ραντεβού;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram με την ιδέα σου. Δεν χρειάζεται να έχεις έτοιμο σχέδιο, αρκεί μια αρχική ιδέα και θα πάμε από εκεί.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Διαλέξεις Tattoo Artist</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέχεις πριν επιλέξεις studio, portfolio, υγιεινή και consultation.</p>
              </Link>
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρης οδηγός για πρωτάρηδες, από την επιλογή σχεδίου μέχρι την εκτέλεση.</p>
              </Link>
              <Link to="/blog/tattoo-studio-athina-i-peiraias" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Tattoo Studio: Αθήνα ή Πειραιάς;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς αποφασίζεις σωστά ανάμεσα στις δύο περιοχές.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/walk-in-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Walk-In Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
